import { TextField } from '@mui/material'
import { isEqual } from 'lodash'
import { memo, useCallback, useEffect, useMemo, useState } from 'react'

const PLACEHOLDERS = {
  CPF: '000.000.000-00',
  CNPJ: '00.000.000/0000-00',
  RG: '00.000.000-0',
  OUTROS: '',
}

const CONFIG = {
  CPF: {
    maxLen: 14,
    clean: (v) => v.replace(/\D/g, '').slice(0, 11),
    format: (v) => {
      const c = v.replace(/\D/g, '').slice(0, 11)
      return c
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})$/, '$1-$2')
    },
  },
  CNPJ: {
    maxLen: 18,
    clean: (v) => v.replace(/[^0-9a-zA-Z]/g, '').slice(0, 14),
    format: (v) => {
      const c = v.replace(/[^0-9a-zA-Z]/g, '').slice(0, 14)
      return c
        .replace(/(\w{2})(\w)/, '$1.$2')
        .replace(/(\w{2}\.\w{3})(\w)/, '$1.$2')
        .replace(/(\w{2}\.\w{3}\.\w{3})(\w)/, '$1/$2')
        .replace(/(\w{2}\.\w{3}\.\w{3}\/\w{4})(\w)/, '$1-$2')
    },
  },
  RG: {
    maxLen: 12,
    clean: (v) => v.replace(/[^0-9a-zA-Z]/g, '').slice(0, 12),
    format: (v) => {
      const c = v.replace(/[^0-9a-zA-Z]/g, '').slice(0, 12)
      return c
        .replace(/(\w{2})(\w)/, '$1.$2')
        .replace(/(\w{2}\.\w{3})(\w)/, '$1.$2')
        .replace(/(\w{2}\.\w{3}\.\w{3})(\w)/, '$1-$2')
    },
  },
  OUTROS: {
    maxLen: undefined,
    clean: (v) => v,
    format: (v) => v,
  },
}

const detectTipo = (raw, tipos) => {
  const list = Array.isArray(tipos) ? tipos : [tipos]
  const stripped = raw.replace(/[^0-9a-zA-Z]/g, '')
  const hasLetters = /[a-zA-Z]/.test(stripped)
  const len = stripped.length

  if (hasLetters) {
    if (list.includes('CNPJ')) return 'CNPJ'
    if (list.includes('RG')) return 'RG'
    return list[0]
  }

  if (list.includes('CPF') && len <= 11) return 'CPF'
  if (list.includes('CNPJ')) return 'CNPJ'
  if (list.includes('RG') && len <= 12) return 'RG'

  return list[0]
}

const formatLabel = (tipos) => {
  const list = Array.isArray(tipos) ? tipos : [tipos]
  return list.join('/')
}

const DocumentField = ({
  value,
  onChange = () => {},
  tipoDocumento = 'CPF',
  label,
  ...props
}) => {
  const tipos = useMemo(
    () => (Array.isArray(tipoDocumento) ? tipoDocumento : [tipoDocumento]),
    [tipoDocumento],
  )

  const tipo = useMemo(() => detectTipo(value || '', tipos), [value, tipos])
  const config = CONFIG[tipo] || CONFIG.CPF

  const [inputValue, setInputValue] = useState('')

  useEffect(() => {
    setInputValue(config.format(value || ''))
  }, [value, config])

  const handleChange = useCallback(
    (e) => {
      const raw = e.target.value
      const newTipo = detectTipo(raw, tipos)
      const newConfig = CONFIG[newTipo] || CONFIG.CPF
      const cleaned = newConfig.clean(raw)
      setInputValue(newConfig.format(cleaned))
      onChange(cleaned)
    },
    [tipos, onChange],
  )

  return (
    <TextField
      value={inputValue}
      onChange={handleChange}
      label={label || formatLabel(tipos)}
      placeholder={PLACEHOLDERS[tipo] || ''}
      inputProps={config.maxLen ? { maxLength: config.maxLen } : undefined}
      {...props}
    />
  )
}

export default memo(DocumentField, isEqual)
